import i18n from '../../plugins/i18n';
import store from '../../store';
import { Search } from './search';

import { Currency } from './settings';

export const GENIUS_PROGRAM = 'Genius';

export namespace Filter {
  export enum SingleForceEvent {
    Map = 'fm',
    Clear = 'fc',
  }

  export interface RouteUpdateCallback {
    needUpdate?: boolean;
    filtersChanged?: boolean;
    currencyChanged?: boolean;
    localeChanged?: boolean;
    forceMap?: boolean;
    forceClear?: boolean;
  }
  export interface Query {
    stars?: number[];
    rating?: number;
    selectableFilters?: {
      key: Filter.SelectableKeys;
      items:
        | Filter.Option[]
        | Filter.Facility[]
        | Filter.HotelType[]
        | Filter.RewardProgram[];
    }[];
    priceRange?: {
      left: number;
      right: number;
    };
    sorting?: number;
    bounds?: {
      sw: number[];
      ne: number[];
    };

    locale?: string;
    currency?: string;
    forceMap?: boolean;
    forceClear?: boolean;
  }

  export interface MinifiedQuery {
    s?: string; // stars
    r?: number; // rating
    opt?: string; // options
    f?: string; // facilities
    ht?: string; // hotel types
    rp?: string; // reward program
    pr?: string; // left, right
    so?: number; // sorting
    b?: string; // lat,lng;lat,lng
    intl?: string; // language
    crr?: string; // currency
    fe?: string; // force events joined by comma
  }
  export interface HotelFilter {
    stars: Stars[];
    rating: Rating[];
    options: Option[];
    facilities: Facility[];
    hotelTypes: HotelType[];
    rewardProgram: RewardProgram[];
    priceRange?: PriceRange;
    sorting: SortBy;
  }
  export class HotelCollectionFilterModel implements HotelFilter {
    stars: Stars[];
    rating: Rating[];
    options: Option[];
    facilities: Facility[];
    hotelTypes: HotelType[];
    rewardProgram: RewardProgram[];
    priceRange?: PriceRange;
    sorting: SortBy;

    constructor(filter: HotelFilter) {
      this.stars = filter.stars;
      this.rating = filter.rating ?? [];
      this.options = filter.options ?? [];
      this.facilities = filter.facilities ?? [];

      this.hotelTypes = filter.hotelTypes
        ? filter.hotelTypes.sort((a, b) =>
            a.name > b.name ? 1 : a.name < b.name ? -1 : 0,
          )
        : [];
      this.rewardProgram = filter.rewardProgram ?? [];
      this.priceRange = filter.priceRange ?? null;
      this.sorting = filter.sorting ?? SortBy.Recommended;
    }

    static buildPriceRange = (currency: Currency.CurrencyItem) => {
      const step =
        (currency.currency === Currency.CURRENCIES.HUF ? 20000 : 50) * 100;
      return PriceRange.compose({ min: 0, max: step * 13, currency: currency });
    };

    static compose() {
      const currency = store.state.support.currency as Currency.CurrencyItem;

      const baseFilters = HotelCollectionFilterModel.composeBase().copyWith({
        priceRange: HotelCollectionFilterModel.buildPriceRange(currency),
      });

      return baseFilters;
    }

    static unminify(payload: MinifiedQuery): Filter.Query {
      try {
        const baseFilters = HotelCollectionFilterModel.compose();

        const query: Filter.Query = {};

        if (payload.s !== undefined) {
          const stars = payload.s?.split(',').filter(it => !!it) ?? [];
          query.stars = stars.map(it => parseInt(it));
        }

        if (payload.r !== undefined) {
          query.rating = payload.r;
        }

        const options = payload.opt?.split(',') ?? [];

        query.selectableFilters = [
          {
            key: SelectableKeys.Options,
            items: baseFilters.options.map(it => {
              it.selected = options.includes(it.id.toString());
              return it;
            }),
          },
        ];

        if (payload.f) {
          const facilities = payload.f.split(',');
          query.selectableFilters.push({
            key: SelectableKeys.Facilities,
            items: baseFilters.facilities.map(it => {
              it.selected = facilities.includes(it.id.toString());
              return it;
            }),
          });
        }

        if (payload.ht) {
          const hotelTypes = payload.ht.split(',');
          query.selectableFilters.push({
            key: SelectableKeys.HotelTypes,
            items: baseFilters.hotelTypes.map(it => {
              it.selected = hotelTypes.includes(it.id.toString());
              return it;
            }),
          });
        }

        if (payload.rp) {
          const rewardPrograms = payload.rp.split(',');
          query.selectableFilters.push({
            key: SelectableKeys.RewardProgram,
            items: baseFilters.rewardProgram.map(it => {
              it.selected = rewardPrograms.includes(it.id.toString());
              return it;
            }),
          });
        }

        if (payload.pr) {
          const priceRange = payload.pr.split(',');
          query.priceRange = {
            left: parseInt(priceRange[0]),
            right: parseInt(priceRange[1]),
          };
        }

        if (payload.so !== undefined) {
          query.sorting = payload.so;
        }

        if (payload.crr) {
          query.currency = payload.crr;
        }

        if (payload.intl) {
          query.locale = payload.intl;
        }

        if (payload.fe) {
          const forceEvents = payload.fe.split(',');
          query.forceClear = forceEvents.includes(SingleForceEvent.Clear);
          query.forceMap = forceEvents.includes(SingleForceEvent.Map);
        }

        return query;
      } catch (error) {
        return {};
      }
    }

    static composeBase() {
      return new HotelCollectionFilterModel({
        stars: Stars.compose(),
        rating: Rating.compose(),
        options: [
          OptionType.FreeCancellation,
          OptionType.FreeBreakfast,
          OptionType.IsGenius,
          OptionType.PayNow,
          OptionType.PayLater,
          OptionType.UseMap,
        ].map(
          it =>
            new Option({
              id: it,
              name: i18n.global.t(`filters.options.${it}`),
              selected: false,
            }),
        ),
        facilities: facilitiesIds
          .map(
            it =>
              new Facility({
                id: it,
                name: i18n.global.t(`filters.ammenites.${it}`),
              }),
          )
          .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0)),
        hotelTypes: [
          AccommodationType.Hotel,
          AccommodationType.Aparthotel,
          AccommodationType.Other,
        ].map(
          it =>
            new HotelType({
              id: it,
              name: i18n.global.t(`filters.accommodationTypes.${it}`),
            }),
        ),
        rewardProgram: rewardPrograms
          .map(it => new RewardProgram({ id: it, name: it }))
          .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0)),
        sorting: SortBy.Recommended,
      });
    }

    isDefault() {
      const priceRangeIsDefault = this.priceRange
        ? this.priceRange.left === this.priceRange.min &&
          this.priceRange.right === this.priceRange.max
        : true;

      const options = this.options.every(it => !it.selected);

      return (
        priceRangeIsDefault &&
        this.stars.every(it => !it.selected) &&
        this.rating.some(it => it.type === RatingType.Any && it.selected) &&
        options &&
        this.facilities.every(it => !it.selected) &&
        this.hotelTypes.every(it => !it.selected) &&
        this.rewardProgram.every(it => !it.selected)
      );
    }

    copyWith({
      stars,
      rating,
      options,
      facilities,
      hotelTypes,
      rewardProgram,
      priceRange,
      sorting,
    }: {
      stars?: Stars[];
      rating?: Rating[];
      options?: Option[];
      facilities?: Facility[];
      hotelTypes?: HotelType[];
      rewardProgram?: RewardProgram[];
      priceRange?: PriceRange;
      sorting?: SortBy;
    }) {
      return new HotelCollectionFilterModel({
        stars: stars ?? this.stars,
        rating: rating ?? this.rating,
        options: options ?? this.options,
        facilities: facilities ?? this.facilities,
        hotelTypes: hotelTypes ?? this.hotelTypes,
        rewardProgram: rewardProgram ?? this.rewardProgram,
        priceRange: priceRange ?? this.priceRange,
        sorting: sorting ?? this.sorting,
      });
    }

    getRatingType(): number {
      return this.rating.find(it => it.selected)?.type;
    }

    getStarsType(): number[] {
      return this.stars.filter(it => it.selected).map(it => it.type);
    }

    useMap(): boolean {
      return (
        this.options.find(it => it.id === OptionType.UseMap)?.selected ?? false
      );
    }

    equals(other: HotelCollectionFilterModel): boolean {
      return (
        this.stars.every(
          (it, index) => it.selected === other.stars[index].selected,
        ) &&
        this.rating.every(
          (it, index) => it.selected === other.rating[index].selected,
        ) &&
        this.options.every(
          (it, index) => it.selected === other.options[index].selected,
        ) &&
        this.facilities.every(
          (it, index) => it.selected === other.facilities[index].selected,
        ) &&
        this.hotelTypes.every(
          (it, index) => it.selected === other.hotelTypes[index].selected,
        ) &&
        this.rewardProgram.every(
          (it, index) => it.selected === other.rewardProgram[index].selected,
        ) &&
        this.priceRange?.left === other.priceRange?.left &&
        this.priceRange?.right === other.priceRange?.right &&
        this.sorting === other.sorting
      );
    }

    convertToQuery(): Query {
      const query: Query = {
        stars: this.getStarsType(),
        rating: this.getRatingType(),
        selectableFilters: [
          {
            key: SelectableKeys.Options,
            items: this.options,
          },
          {
            key: SelectableKeys.Facilities,
            items: this.facilities,
          },
          {
            key: SelectableKeys.HotelTypes,
            items: this.hotelTypes,
          },
          {
            key: SelectableKeys.RewardProgram,
            items: this.rewardProgram,
          },
        ],

        sorting: this.sorting,
      };

      if (this.priceRange) {
        query.priceRange = {
          left: this.priceRange.left,
          right: this.priceRange.right,
        };
      }

      return query;
    }

    minify(): MinifiedQuery {
      const priceRange = this.priceRange
        ? `${this.priceRange.left},${this.priceRange.right}`
        : null;

      const stars = this.stars
        .filter(it => it.selected)
        .map(it => it.type)
        .join(',');

      const options = this.options
        .filter(it => it.selected)
        .map(it => it.id)
        .join(',');

      const facilities = this.facilities
        .filter(it => it.selected)
        .map(it => it.id)
        .join(',');

      const hotelTypes = this.hotelTypes
        .filter(it => it.selected)
        .map(it => it.id)
        .join(',');

      const rewardProgram = this.rewardProgram
        .filter(it => it.selected)
        .map(it => it.id)
        .join(',');

      const payload = {
        s: stars,
        r: this.getRatingType(),
        opt: options,
        f: facilities,
        ht: hotelTypes,
        rp: rewardProgram,
        pr: priceRange,
        so: this.sorting,
      };

      Object.keys(payload).forEach(key => {
        if (
          !payload[key] &&
          key !== 'pr' &&
          key !== 'so' &&
          key !== 's' &&
          key !== 'r'
        ) {
          delete payload[key];
        }
      });

      return payload;
    }
  }

  export class Selectable {
    selected: boolean = false;
    disabled?: boolean;

    constructor(selected: boolean) {
      this.selected = selected;
    }

    toggle(force?: boolean) {
      if (force === undefined) {
        this.selected = !this.selected;
      } else {
        this.selected = force;
      }
    }
  }

  export enum StarsType {
    Any = 0,
    One = 1,
    Two = 2,
    Three = 3,
    Four = 4,
    Five = 5,
  }

  export class Stars extends Selectable {
    type: StarsType;

    constructor(type: StarsType, selected: boolean) {
      super(selected);
      this.type = type;
    }

    static compose(_selected?: StarsType[]): Stars[] {
      let stored = Search.HotelCategorySurvey.get();
      if (
        stored &&
        !Array.isArray(stored.value) &&
        Object.values(StarsType).includes(stored.value)
      ) {
        Search.HotelCategorySurvey.save([stored.value]);
        stored = { ...stored, value: [stored.value] };
      }

      const selected = _selected ?? stored?.value;
      return [
        new Stars(StarsType.One, selected?.includes(StarsType.One)),
        new Stars(StarsType.Two, selected?.includes(StarsType.Two)),
        new Stars(StarsType.Three, selected?.includes(StarsType.Three)),
        new Stars(StarsType.Four, selected?.includes(StarsType.Four)),
        new Stars(StarsType.Five, selected?.includes(StarsType.Five)),
      ];
    }

    copyWith({ type }: { type: StarsType }): Stars {
      return new Stars(type, this.selected);
    }
  }

  export enum RatingType {
    Any = 0,
    ThreeHalf = 7.0,
    Four = 8.0,
    FourHalf = 9.0,
  }
  export class Rating extends Selectable {
    type: RatingType;

    constructor(type: RatingType, selected: boolean) {
      super(selected);
      this.type = type;
    }

    raw() {
      return this.type;
    }

    static compose(ratingType?: RatingType): Rating[] {
      ratingType = ratingType ?? RatingType.Any;
      return [
        new Rating(RatingType.Any, ratingType === RatingType.Any),
        new Rating(RatingType.ThreeHalf, ratingType === RatingType.ThreeHalf),
        new Rating(RatingType.Four, ratingType === RatingType.Four),
        new Rating(RatingType.FourHalf, ratingType === RatingType.FourHalf),
      ];
    }
  }

  export class PriceRange {
    min: number;
    max: number;
    left: number;
    right: number;
    currency: Currency.CurrencyItem;

    constructor(
      min: number,
      max: number,
      left: number,
      right: number,
      currency: Currency.CurrencyItem,
    ) {
      this.min = min;
      this.max = max;
      this.left = left;
      this.right = right;
      this.currency = currency;
    }

    static compose({
      min,
      max,
      currency,
    }: {
      min: number;
      max: number;
      currency: Currency.CurrencyItem;
    }): PriceRange {
      return new PriceRange(min, max, min, max, currency);
    }

    copyWith({ left, right }: { left: number; right: number }): PriceRange {
      return new PriceRange(this.min, this.max, left, right, this.currency);
    }
  }

  export class Option extends Selectable {
    name: string;
    id: string | number;

    constructor({
      id,
      name,
      selected,
    }: {
      id: string | number;
      name: string;
      selected?: boolean;
    }) {
      super(selected ?? false);
      this.id = id;
      this.name = name;
    }

    relocalize(): Option {
      const name = i18n.global.t(`filters.options.${this.id}`);

      return new Option({
        id: this.id,
        name: name,
        selected: this.selected,
      });
    }
  }

  export class Facility extends Selectable {
    name: string;
    id: string | number;

    constructor({
      id,
      name,
      selected,
    }: {
      id: string | number;
      name: string;
      selected?: boolean;
    }) {
      super(selected ?? false);
      this.id = id;
      this.name = name;
    }

    relocalize(): Facility {
      const name = i18n.global.t(`filters.ammenites.${this.id}`);

      return new Facility({
        id: this.id,
        name: name,
        selected: this.selected,
      });
    }
  }

  export class HotelType extends Selectable {
    name: string;
    id: string | number;

    constructor({
      id,
      name,
      selected,
    }: {
      id: string | number;
      name: string;
      selected?: boolean;
    }) {
      super(selected ?? false);
      this.name = name;
      this.id = id;
    }

    relocalize(): HotelType {
      const name = i18n.global.t(`filters.accommodationTypes.${this.id}`);

      return new HotelType({
        id: this.id,
        name: name,
        selected: this.selected,
      });
    }
  }

  export class RewardProgram extends Selectable {
    name: string;
    id: string | number;

    constructor({
      id,
      name,
      selected,
    }: {
      id: string | number;
      name: string;
      selected?: boolean;
    }) {
      super(selected ?? false);
      this.id = id;
      this.name = name;
    }

    relocalize(): RewardProgram {
      const name = i18n.global.t(`filters.rewardPrograms.${this.id}`);

      return new RewardProgram({
        id: this.id,
        name: name,
        selected: this.selected,
      });
    }
  }

  export enum SelectableKeys {
    Options = 'options',
    Facilities = 'facilities',
    HotelTypes = 'hotelTypes',
    RewardProgram = 'rewardProgram',
  }

  export enum OptionType {
    FreeCancellation = 'freeCancellation',
    FreeBreakfast = 'freeBreakfast',
    IsGenius = 'isGenius',
    PayNow = 'payNow',
    PayLater = 'payLater',
    UseMap = 'useMap',
  }

  export enum AccommodationType {
    Hotel = 'hotel',
    Aparthotel = 'aparthotel',
    Other = 'other',
  }

  export enum SortBy {
    Recommended = 0,
    Reviews = 1,
    Price = 2,
    Savings = 3,
  }

  export const facilitiesIds = [
    'RS',
    'RT',
    'FI',
    'PI',
    'BL',
    'FC',
    'CE',
    'DC',
    'LS',
    'MR',
    'ML',
    'PA',
    'WA',
    'PL',
    'BR',
    'BC',
    'CF',
    'HP',
    'OP',
    'SP',
    'AT',
    'BB',
    'CA',
    'HT',
    'IP',
    'SB',
    'TC',
    'AP',
    'SA',
    'BA',
    'GC',
    'NH',
    'CG',
    'KP',
    'SL',
    'EC',
    'SS',
    'AC',
    'KT',
    'MB',
    'FR',
    'ST',
    'RR',
    'MW',
  ];
}

const allowedFacilities = [
  'FP',
  'BC',
  'MR',
  'RT',
  'BL',
  'DC',
  'LS',
  'PL',
  'FC',
  'SA',
  'EC',
  'CE',
];

const rewardPrograms = [
  'Accor Live Limitless (ALL)',
  'IHG Rewards',
  'Best Western Rewards',
  'Marriott Bonvoy',
  'Hilton Honors',
  'Le Club AccorHotels',
  'Flavours Instant Benefit',
  'Radisson Rewards',
  'NH Rewards',
  'Star Traveler',
  'Melia Rewards',
  'Choice Privileges',
  'I Prefer Hotel Rewards',
  'Wyndham Rewards',
  'Scandic Friends',
  'AdvantageCLUB',
  'NH DISCOVERY',
  'L.E. Hotels Rewards',
  'H Rewards',
  'World of Hyatt',
  'Thon+',
  'Ascott Star Rewards',
  'Worldhotels Rewards',
  'MeliaRewards',
  'Jurys Reward',
  'I AM STAR',
  'WorldHotels Rewards',
  'Kempinski Discovery',
  'Fraser World',
  'DISCOVERY Loyalty',
  'Rocco Forte Friends',
  'iPrefer',
  'Fan of M.O.',
  'My Millennium',
  'Jumeirah One',
  'Discovery',
  'The Montcalm Club',
  'Hello Rewards',
  'CQ Rewards',
  'Sercotel Rewards',
  'myBarcelo',
  'Golden Circle',
  'Taj InnerCircle',
  'RSVP Rewards',
  'Leaders Club',
  'Millennium Rewards',
  'Club 5C',
  'iPrefer Rewards',
];
